import * as React from "react";
import { TabletWrapper, RightNavBarWrapper, LeftNavBarWrapper, CenterNavBarWrapper } from "./tablet-container-style";

import ScreenContentRenderer from "../../views/screen-content-renderer/screen-content-renderer";
import { AppNavigationModel } from "../../../lib/navigation-model";
import { RenderEnvironment } from "common/components/types";
import { TabDescription } from "common/generator/description";
import GlideAppBar from "../glide-app-bar/glide-app-bar";
import { firstNonHiddenNonFlyoutTabIndex, SerializedApp, ScreenFlag } from "common/components/SerializedApp";
import { AppBarContext } from "../../../lib/app-utility-context";
import { defined } from "common/Support";
import { styled } from "../../../styled-components";
import ThemeContainer from "../../views/theme-container/theme-container";
import { DeviceFormFactor } from "common/render/form-factor";
import { usePreviousValue } from "hooks/use-previous-prop";
import { getFeatureSetting } from "common/feature-settings";
import { MapContext } from "hooks/use-map-context";

interface Props {
    serializedApp: SerializedApp;
    tabs: readonly TabDescription[];
    appBarContext?: AppBarContext;
    onMenuClicked?: () => void;
    hasFlyout: boolean;
    hasTabBar: boolean;
    renderEnvironment: RenderEnvironment;
    currentNavigationModel: AppNavigationModel;
    currentTab: TabDescription;
    expandLeftPanel: boolean;
}

// this is a terrible hack, jason to add notes
const TabletContainerHack = styled.div<{ itemID: string }>`
    & .selected-item-list-item-${p => p.itemID} {
        background-color: ${p => p.theme.listItem.selectedColor};
    }
`;

function canDoublePop(m: AppNavigationModel): boolean {
    return m.canCurrentScreenPop && m.popScreen().canCurrentScreenPop;
}

function getItemIDForModel(navModel: AppNavigationModel | undefined): string | undefined {
    if (navModel === undefined) return undefined;

    let itemID = navModel.canCurrentScreenPop ? navModel.currentScreen?.itemID : undefined;
    while (canDoublePop(navModel)) {
        navModel = navModel.popScreen();
        itemID = navModel.currentScreen?.itemID;
    }
    return itemID;
}

const TabletContainer: React.FC<Props> = p => {
    const {
        currentTab,
        renderEnvironment,
        hasFlyout,
        hasTabBar,
        expandLeftPanel,
        onMenuClicked,
        tabs,
        currentNavigationModel,
        serializedApp,
        appBarContext,
    } = p;
    const { appEnvironment } = renderEnvironment;
    const previousNavigationModel = usePreviousValue(currentNavigationModel);

    if (currentNavigationModel === undefined) return null;

    // get a bunch of useful states
    const currentIsInModal = currentNavigationModel.isInModal();
    const currentModalNavModel = currentIsInModal ? currentNavigationModel : undefined;
    const prevModalNavModel = previousNavigationModel?.isInModal() ? previousNavigationModel : undefined;
    const rootModel = currentNavigationModel.popModalsToRoot();
    const poppedToRootModel = rootModel.popToRoot();

    const rootCanPop = rootModel.canCurrentScreenPop;
    const rootIsArray = poppedToRootModel.currentScreenHasFlag(ScreenFlag.IsArrayScreen);
    const rootIsMap = poppedToRootModel.currentScreenHasFlag(ScreenFlag.IsMapScreen);

    const shouldRenderMaster = rootIsArray;

    const masterModel = shouldRenderMaster ? poppedToRootModel : undefined;
    const detailModel = rootCanPop || !shouldRenderMaster ? rootModel : undefined;

    const needsPush = masterModel !== undefined && detailModel === undefined;
    const isModalOpen = currentModalNavModel !== undefined;

    let masterNode: React.ReactNode;
    let detailNode: React.ReactNode;
    let modalNode: React.ReactNode;

    if (getFeatureSetting("mapTabletLayout") && rootIsMap) {
        const mapMasterModel = rootModel;
        const mapMasterScreen = mapMasterModel.currentScreen;

        const mapDetailModel = poppedToRootModel;
        const mapDetailScreen = mapDetailModel.currentScreen;

        // master is right
        masterNode = mapMasterScreen !== undefined && (
            <>
                <LeftNavBarWrapper expandLeftPanel={expandLeftPanel} aria-hidden={isModalOpen}>
                    <GlideAppBar
                        isSubScreen={mapMasterModel.canCurrentScreenPop}
                        renderEnv={renderEnvironment.with(
                            {
                                sizeClass: DeviceFormFactor.Tablet,
                                displayContext: "map-master",
                            },
                            mapMasterScreen.flags
                        )}
                        isOnFirstTab={mapMasterModel.currentTabIndex === firstNonHiddenNonFlyoutTabIndex(tabs)}
                        appTitle={serializedApp.title}
                        showShareButton={false}
                        screen={mapMasterScreen}
                        overrideContext={appBarContext}
                        onMenuClicked={onMenuClicked}
                    />
                </LeftNavBarWrapper>
                <div className={"left-panel"} key={"left-panel"}>
                    <ScreenContentRenderer
                        key={mapMasterScreen.screenId + "screenmaster"}
                        screen={mapMasterScreen}
                        inTab={hasTabBar && !currentIsInModal}
                        idSuffix={"master"}
                        isSubScreen={true}
                        renderEnvironment={renderEnvironment.with(
                            {
                                sizeClass: DeviceFormFactor.Tablet,
                                displayContext: "map-master",
                            },
                            mapMasterScreen.flags
                        )}
                        currentTab={currentTab}
                    />
                </div>
            </>
        );
        const idArray: string[] = [mapMasterScreen?.itemID ?? "_NOT_FOUND_"];
        let popped = mapMasterModel;
        while (popped.canCurrentScreenPop) {
            popped = popped.popScreen();
            idArray.unshift(popped.currentScreen?.itemID ?? "_NOT_FOUND_");
        }
        // detail is left
        detailNode = mapDetailScreen !== undefined && (
            <MapContext.Provider value={idArray}>
                <TabletContainerHack
                    itemID={"none"}
                    className={"right-panel"}
                    role="main"
                    aria-label={currentTab.title ?? "Left Panel"}
                    aria-hidden={isModalOpen}>
                    <ScreenContentRenderer
                        key={mapDetailScreen.screenId + "screendetail"}
                        screen={mapDetailScreen}
                        inTab={hasTabBar}
                        isSubScreen={false}
                        renderEnvironment={renderEnvironment.with(
                            {
                                sizeClass: DeviceFormFactor.Tablet,
                                displayContext: "map-detail",
                                needsPush,
                            },
                            mapDetailScreen.flags
                        )}
                        currentTab={currentTab}
                    />
                </TabletContainerHack>
            </MapContext.Provider>
        );
    } else {
        if (masterModel !== undefined) {
            const masterScreen = masterModel.currentScreen;
            const detailItemId = getItemIDForModel(detailModel);
            masterNode = masterScreen !== undefined && (
                <>
                    <LeftNavBarWrapper expandLeftPanel={expandLeftPanel} aria-hidden={isModalOpen}>
                        <GlideAppBar
                            isSubScreen={false}
                            renderEnv={renderEnvironment.with(
                                {
                                    sizeClass: DeviceFormFactor.Tablet,
                                    displayContext: "master",
                                },
                                masterScreen.flags
                            )}
                            isOnFirstTab={rootModel.currentTabIndex === firstNonHiddenNonFlyoutTabIndex(tabs)}
                            appTitle={serializedApp.title}
                            showShareButton={!appEnvironment.enableShareScreen}
                            screen={masterScreen}
                            overrideContext={appBarContext}
                            onMenuClicked={hasFlyout ? onMenuClicked : undefined}
                        />
                    </LeftNavBarWrapper>
                    <TabletContainerHack
                        itemID={detailItemId ?? "none"}
                        className={"left-panel"}
                        role="main"
                        aria-label={currentTab.title ?? "Left Panel"}
                        aria-hidden={isModalOpen}>
                        <ScreenContentRenderer
                            key={masterScreen.screenId + "screen"}
                            screen={masterScreen}
                            inTab={hasTabBar}
                            isSubScreen={false}
                            renderEnvironment={renderEnvironment.with(
                                {
                                    sizeClass: DeviceFormFactor.Tablet,
                                    displayContext: "master",
                                    needsPush,
                                },
                                masterScreen.flags
                            )}
                            currentTab={currentTab}
                        />
                    </TabletContainerHack>
                </>
            );
        }

        if (detailModel !== undefined) {
            const detailScreen = detailModel.currentScreen;
            const hasMaster = masterModel !== undefined;

            const panelClass = hasMaster ? "right-panel" : "center-panel";
            const NavBarWrapper = hasMaster ? RightNavBarWrapper : CenterNavBarWrapper;

            // if isSubscreen is true, it will render a back button in the detail navigation bar.
            // There are two scenarios that need to be handled.
            // If the root screen is an array, it will be rendered in the master and the detail screen will be the first push.
            //   In this case, if we navigate via an inline list or other component, the detail will need to navigate back
            //   to the last screen before root, hence the double pop check
            // If the root screen is a detail, we render it here but need to check that it can pop to also render the back button
            const isSubScreen =
                (hasMaster && canDoublePop(detailModel)) || (!hasMaster && detailModel.canCurrentScreenPop);

            // IMPORTANT: Do not remove `key={panelClass}`. For some reason, mobile safari
            // would not update when a new detailNode was create and would produce an empty
            // screen. This fixes that.
            detailNode = detailScreen !== undefined && (
                <>
                    <NavBarWrapper expandLeftPanel={expandLeftPanel} aria-hidden={isModalOpen}>
                        <GlideAppBar
                            isSubScreen={isSubScreen}
                            renderEnv={renderEnvironment.with(
                                {
                                    sizeClass: DeviceFormFactor.Tablet,
                                    displayContext: "detail",
                                },
                                detailScreen.flags
                            )}
                            isOnFirstTab={detailModel.currentTabIndex === firstNonHiddenNonFlyoutTabIndex(tabs)}
                            appTitle={serializedApp.title}
                            showShareButton={false}
                            screen={detailScreen}
                            overrideContext={appBarContext}
                            onMenuClicked={!hasMaster ? onMenuClicked : undefined}
                        />
                    </NavBarWrapper>
                    <div
                        className={panelClass}
                        key={panelClass}
                        role="complementary"
                        aria-label={currentTab.title ?? "Detail Panel"}
                        aria-hidden={isModalOpen}>
                        <div className="content-wrapper">
                            <ScreenContentRenderer
                                key={detailScreen.screenId + "screen"}
                                screen={detailScreen}
                                inTab={hasTabBar && !currentIsInModal}
                                isSubScreen={true}
                                renderEnvironment={renderEnvironment.with(
                                    {
                                        sizeClass: DeviceFormFactor.Tablet,
                                        displayContext: "detail",
                                    },
                                    detailScreen.flags
                                )}
                                currentTab={currentTab}
                            />
                        </div>
                    </div>
                </>
            );
        }
    }

    let modalWidth;
    let modalHeight;
    if (currentModalNavModel !== undefined || prevModalNavModel !== undefined) {
        const modalModel = defined(currentModalNavModel ?? prevModalNavModel);
        const modalIsPrev = currentModalNavModel === undefined;

        const modalScreen = modalModel.currentScreen;

        if (modalScreen?.flags.has(ScreenFlag.IsSignaturePad) === true) {
            modalWidth = 600;
            modalHeight = 364;
        }

        modalNode = modalScreen !== undefined && (
            <div className={"tablet-modal " + (modalIsPrev ? "leave" : "enter")}>
                <div
                    className={"tablet-modal-content"}
                    aria-hidden={modalIsPrev}
                    role="dialog"
                    aria-modal={true}
                    aria-label={currentTab.title}>
                    <div className="tablet-modal-scr">
                        <ScreenContentRenderer
                            key={modalScreen.screenId + "screen"}
                            screen={modalScreen}
                            inTab={false}
                            isSubScreen={false}
                            renderEnvironment={renderEnvironment.with(
                                {
                                    sizeClass: DeviceFormFactor.Tablet,
                                    displayContext: "modal",
                                },
                                modalScreen.flags
                            )}
                            currentTab={currentTab}
                        />
                    </div>
                    <div className="tablet-modal-bar">
                        <GlideAppBar
                            isSubScreen={modalModel.canCurrentScreenPop}
                            renderEnv={renderEnvironment.with(
                                {
                                    sizeClass: DeviceFormFactor.Tablet,
                                    displayContext: "modal",
                                },
                                modalScreen.flags
                            )}
                            isOnFirstTab={false}
                            appTitle={serializedApp.title}
                            showShareButton={false}
                            screen={modalScreen}
                            overrideContext={appBarContext}
                            onMenuClicked={undefined}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <TabletWrapper id="tablet" expandLeftPanel={expandLeftPanel} modalWidth={modalWidth} modalHeight={modalHeight}>
            {masterNode}
            <ThemeContainer useLargeTitles={false} updateTailwind={false}>
                {detailNode}
                {modalNode}
            </ThemeContainer>
        </TabletWrapper>
    );
};

export default TabletContainer;
